import {
  FeatureChangerWrapper,
  FeatureListItemUnderline,
} from 'components/features-page/FeatureSelector'
import Svg from 'components/UI/SVG'
import { GatsbyImage, getImage, ImageDataLike } from 'gatsby-plugin-image'
import FeatureStarIcon from 'images/feature-star.svg'
import { ReactElement, ReactNode, useState } from 'react'
import { Button, Flex, Grid, Link, Text, ThemeUIStyleObject } from 'theme-ui'
import getEnglishStrings from 'utils/getEnglishStrings'

interface SocialProofItem {
  authorName: string | undefined
  authorDescription: string | undefined
  authorPicture: ImageDataLike
  awardBadge: ImageDataLike
  content: string | undefined
  organizationPicture: ImageDataLike
  score: string | undefined
  url?: string
}

interface SocialProofCategory {
  name: string | undefined
  items: SocialProofItem[] | undefined
}

export default function FeatureSocialProof({
  data,
}: {
  data: Queries.DirectusData_component_social_proof
}): ReactElement {
  const title = getEnglishStrings(data?.strings)?.title
  const categories = data?.categories

  const parsedData: SocialProofCategory[] =
    categories?.map(category => {
      const strings = getEnglishStrings(category?.social_proof_categories_id?.strings)
      const categoryName = strings?.name || ''
      const items = category?.social_proof_categories_id?.items?.map(item => {
        const itemStrings = getEnglishStrings(item?.social_proof_items_id?.strings)
        return {
          authorName: itemStrings?.author_name || '',
          authorDescription: itemStrings?.author_description || '',
          authorPicture: item?.social_proof_items_id?.author_picture?.imageFile,
          awardBadge: item?.social_proof_items_id?.award_badge?.imageFile,
          content: itemStrings?.content || '',
          organizationPicture: item?.social_proof_items_id?.organization_picture?.imageFile,
          score: item?.social_proof_items_id?.score || '',
          url: item?.social_proof_items_id?.url,
        }
      })
      return {
        name: categoryName,
        items,
      }
    }) ?? []

  const [selectedTestimonyTab, setSelectedTestimonyTab] = useState<string>(
    parsedData?.[0]?.name ?? '',
  )
  const availableTabs = parsedData?.map(p => p.name ?? '')

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
        gap: '4.8rem',
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          width: '100%',
          alignItems: 'center',
          gap: '4rem',
        }}
      >
        <Text as="h2" variant="h2Big" sx={{ textAlign: 'center', maxWidth: '60rem' }}>
          {title}
        </Text>
        <FeatureChangerWrapper>
          {availableTabs?.map(tab => (
            <Button
              key={tab}
              sx={{
                backgroundColor: 'transparent',
                position: 'relative',
                color: tab === selectedTestimonyTab ? 'white' : 'white50',
                '&:hover': {
                  cursor: 'pointer',
                },
                flex: '0 0 auto',
              }}
              variant="text.paragraphBigBold"
              onClick={() => setSelectedTestimonyTab(tab)}
            >
              {tab}
              {tab === selectedTestimonyTab && <FeatureListItemUnderline />}
            </Button>
          ))}
        </FeatureChangerWrapper>

        <Reviews
          data={parsedData?.find(p => p.name === selectedTestimonyTab)?.items}
          key={selectedTestimonyTab}
        />
      </Flex>
    </Flex>
  )
}

function Wrapper({
  children,
  sxx,
  url,
}: {
  children: ReactNode
  sxx: ThemeUIStyleObject
  url?: string
}): ReactElement {
  return (
    <Link
      sx={{
        flexDirection: 'column',
        p: '2.4rem',
        backgroundColor: 'white05',
        ':hover': {
          backgroundColor: 'white10',
        },
        borderRadius: '1.6rem',
        maxWidth: ['100%', '46.6rem', '46.6rem'],
        ...sxx,
      }}
      href={url}
    >
      {children}
    </Link>
  )
}

function Reviews({ data }: { data: SocialProofItem[] | null | undefined }): ReactElement {
  return (
    <Grid columns={[1, 2]} sx={{ gap: '4.8rem' }}>
      {data?.map(item => {
        const authorImage = getImage(item.authorPicture)
        const organizationImage = getImage(item.organizationPicture)
        const awardBadgeImage = getImage(item.awardBadge)

        return (
          <Wrapper
            sxx={{ flexDirection: 'column', gap: '2.4rem' }}
            key={item.authorName}
            url={item.url ?? undefined}
          >
            <Flex sx={{ gap: '1.2rem', flexDirection: ['column', 'row'] }}>
              <Flex data-testid="images">
                {authorImage && (
                  <GatsbyImage
                    image={authorImage}
                    alt="Author"
                    sx={{ width: '5rem', height: '5rem', borderRadius: '50%' }}
                  />
                )}
                {organizationImage && (
                  <GatsbyImage
                    image={organizationImage}
                    alt="Organization"
                    sx={{
                      width: '5rem',
                      height: '5rem',
                      borderRadius: '50%',
                      marginLeft: '-1.2rem',
                    }}
                  />
                )}
              </Flex>
              <Flex sx={{ flexDirection: 'column' }}>
                <Text variant="paragraphBigBold" sx={{ color: 'white' }}>
                  {item.authorName}
                </Text>
                <Text variant="paragraphSmallRegular" sx={{ color: 'white' }}>
                  {item.authorDescription}
                </Text>
              </Flex>
            </Flex>
            <Text variant="paragraphBigRegular">{item.content}</Text>
            {item.score && (
              <Flex
                sx={{
                  justifyContent: 'space-between',
                  height: '3rem',
                  alignItems: 'center',
                }}
              >
                <Flex sx={{ gap: '0.8rem', alignItems: 'center' }}>
                  <Text variant="paragraphBigBold" sx={{ color: 'yellow' }}>
                    {item.score}
                  </Text>
                  <Svg svg={FeatureStarIcon} />
                </Flex>
                {awardBadgeImage && (
                  <GatsbyImage image={awardBadgeImage} alt="Award Badge" sx={{ width: '8.8rem' }} />
                )}
              </Flex>
            )}
          </Wrapper>
        )
      })}
    </Grid>
  )
}
